import { useEffect, useState } from 'react';

import { getModifierKey, isEditableElementFocused } from '@/lib/utils/platform';

/**
 * Window event that opens the global search from anywhere in the app —
 * the programmatic twin of Cmd/Ctrl+K, for surfaces that render a visible
 * search launcher (the read-only dashboard hero) instead of relying on the
 * shortcut alone.
 */
export const GLOBAL_SEARCH_OPEN_EVENT = 'ahoy:global-search:open';

/** Open the global search palette (same as pressing Cmd/Ctrl+K). */
export function openGlobalSearch(): void {
  window.dispatchEvent(new Event(GLOBAL_SEARCH_OPEN_EVENT));
}

/**
 * Hook to manage global Cmd+K/Ctrl+K search keyboard shortcut
 * Uses capture phase to intercept before page-specific handlers
 *
 * @param disabled - When true, the keyboard shortcut is disabled (e.g., on unauthenticated pages)
 */
export function useGlobalSearch(disabled = false) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (disabled) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }

      if (isEditableElementFocused()) {
        return;
      }

      const cmdOrCtrl = getModifierKey(event);

      // Cmd/Ctrl + K: Toggle global search
      if (cmdOrCtrl && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(prev => !prev);
      }
    };

    const handleOpenRequest = () => setIsOpen(true);

    // Use capture phase to intercept before other handlers
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener(GLOBAL_SEARCH_OPEN_EVENT, handleOpenRequest);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener(GLOBAL_SEARCH_OPEN_EVENT, handleOpenRequest);
    };
  }, [disabled]);

  const close = () => setIsOpen(false);

  return { isOpen, close };
}
